import ApiClient from "../ApiClient";
import NewUUID from "../../utils/randy/NewUUID";

/**
 * Get all rule history entries for a bill
 * @param {string} billId - UUID of the bill
 * @returns {Promise<Array>} Array of history entries
 */
export const getRuleHistory = async (billId) => {
    const response = await ApiClient.get(`/api/bills/templates/${billId}/rule-history`, {
        headers: {
            accept: "application/json",
            request_id: NewUUID(),
        },
    });
    return response.data.result;
};

/**
 * Save current rule state manually
 * @param {string} billId - UUID of the bill
 * @param {string} csrfToken - CSRF token
 * @returns {Promise<string>} History entry ID on success
 */
export const saveRuleHistory = async (billId, csrfToken) => {
    const response = await ApiClient.post(
        `/api/bills/templates/${billId}/rule-history/save`,
        {},
        {
            headers: {
                accept: "application/json",
                request_id: NewUUID(),
                "X-CSRF-Token": csrfToken,
            },
        }
    );
    return response.data.result;
};

/**
 * Restore a bill to a previous rule state
 * @param {string} billId - UUID of the bill
 * @param {string} historyId - UUID of the history entry to restore
 * @param {string} csrfToken - CSRF token
 * @returns {Promise<string>} "success" on success
 */
export const restoreRuleHistory = async (billId, historyId, csrfToken) => {
    const response = await ApiClient.post(
        `/api/bills/templates/${billId}/rule-history/${historyId}/restore`,
        {},
        {
            headers: {
                accept: "application/json",
                request_id: NewUUID(),
                "X-CSRF-Token": csrfToken,
            },
        }
    );
    return response.data.result;
};

/**
 * Rename (set label) for a rule history entry
 * @param {string} billId - UUID of the bill
 * @param {string} historyId - UUID of the history entry
 * @param {string|null} label - New label; null or empty to clear
 * @param {string} csrfToken - CSRF token
 * @returns {Promise<Object>} Updated history entry
 */
export const renameRuleHistory = async (billId, historyId, label, csrfToken) => {
    const response = await ApiClient.put(
        `/api/bills/templates/${billId}/rule-history/${historyId}`,
        { label: label || null },
        {
            headers: {
                accept: "application/json",
                request_id: NewUUID(),
                "X-CSRF-Token": csrfToken,
            },
        }
    );
    return response.data.result;
};

/**
 * Delete (soft-delete) a rule history entry
 * @param {string} billId - UUID of the bill
 * @param {string} historyId - UUID of the history entry
 * @param {string} csrfToken - CSRF token
 * @returns {Promise<string>} "success" on success
 */
export const deleteRuleHistory = async (billId, historyId, csrfToken) => {
    const response = await ApiClient.delete(
        `/api/bills/templates/${billId}/rule-history/${historyId}`,
        {
            headers: {
                accept: "application/json",
                request_id: NewUUID(),
                "X-CSRF-Token": csrfToken,
            },
        }
    );
    return response.data.result;
};
